import React, { useState, useMemo, useEffect } from 'react';
import { X, Save, AlertCircle, CheckCircle2, Wallet } from 'lucide-react';
import Button from '../../../../ui-kit/Button';
import { getDatabase, ref, update } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { isCodOrder, codAdvance, codCollectable, isAdvancePaid } from '../../logic/codAmount';
import { advanceOnlyInRemark } from '../../logic/advanceInRemark';
import { extractAwb } from '../shared/courierUtils';
import { usePartialCodConfig } from '../../hooks/usePartialCodConfig';
import { closePaymentLink } from '../../services/deskActions';
import { authHeaders } from '../../../../core/api/authHeaders';

const PROXY_BASE = (import.meta.env.VITE_PROXY_URL || (import.meta.env.DEV ? 'http://localhost:3001' : '')).replace(/\/$/, '');

const rs = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

/**
 * PartialCodModal — record a partial-COD advance that was paid MANUALLY (straight to
 * our bank / UPI), with its UTR. Collecting the advance online is the Payment link
 * window's job: its "Partial COD · collect ₹99 advance" option puts the advance on the
 * order and records Cashfree's UTR by itself.
 *
 * Money rules enforced here:
 *   · Only COD orders can carry an advance.
 *   · The advance can never be the whole total (that is just a prepaid order).
 *   · The UTR is REQUIRED — an advance without one cannot be matched to the bank.
 *   · Once the parcel has an AWB the amount is frozen — the courier has already
 *     been told what to collect, so changing it now would mis-collect cash.
 *   · Recording the advance as received is what unlocks courier assignment
 *     (see dispatchReadiness). Setting 0 removes an UNPAID advance (full COD again).
 */
const PartialCodModal = ({ isOpen, onClose, order, onUpdate }) => {
    const total = useMemo(() => Math.round(Number(order?.payment?.total ?? order?.total ?? 0)), [order]);
    const existing = useMemo(() => (order ? codAdvance(order) : 0), [order]);
    const alreadyPaid = useMemo(() => (order ? existing > 0 && isAdvancePaid(order) : false), [order, existing]);
    const partialCod = usePartialCodConfig();

    const [advance, setAdvance] = useState(String(existing || ''));
    const [reference, setReference] = useState('');
    const [loading, setLoading] = useState(false);

    // Re-read the order's advance every time the dialog opens.
    //
    // useState only takes its value on the FIRST render, and this dialog stays
    // mounted with order=undefined until it is opened — so it showed a blank
    // field on an order that already had an advance. Saving from there would
    // have WIPED the advance and told the courier to collect the full amount.
    useEffect(() => {
        if (!isOpen || !order) return;
        setAdvance(String(existing || partialCod.defaultAdvance || ''));
        setReference('');
    }, [isOpen, order, existing, partialCod.defaultAdvance]);

    if (!isOpen || !order) return null;

    const awb = extractAwb(order);
    const remarkFlag = advanceOnlyInRemark(order);
    const notCod = !isCodOrder(order);
    const advanceNum = Math.max(0, Math.round(Number(advance) || 0));
    const collects = Math.max(0, total - advanceNum);
    const tooBig = advanceNum > 0 && advanceNum >= total;
    const clearing = advanceNum === 0;
    const utr = reference.trim();
    const utrOk = /^[A-Za-z0-9]{6,30}$/.test(utr);

    const blocked = notCod
        ? 'This is not a COD order — there is nothing for a courier to collect, so an advance does not apply.'
        : awb
            ? `This parcel is already booked (AWB ${awb}). The courier has been told to collect ${rs(codCollectable(order))} — that amount can no longer be changed here.`
            : null;

    const handleSave = async () => {
        if (blocked || alreadyPaid) return;
        if (tooBig) { alert(`The advance (${rs(advanceNum)}) cannot be the whole order (${rs(total)}).\n\nFor a fully prepaid order, change the payment mode instead.`); return; }
        if (!clearing && !utrOk) { alert('Enter the UTR / UPI reference of the advance (6–30 letters or digits). It is how the money is matched to the bank statement.'); return; }
        setLoading(true);
        try {
            const db = getDatabase();
            const key = order.firebaseKey || order.id;
            const who = getAuth().currentUser?.displayName || getAuth().currentUser?.email || 'Admin';
            const now = new Date().toISOString();

            // The advance itself (amount, received, UTR, who) is written by the proxy with
            // the verified login — the database refuses "advance PAID" from a browser
            // since the 1 Nov 2026 security review. Removing an unpaid advance returns
            // the order to plain full COD.
            const r = await fetch(`${PROXY_BASE}/api/orders/${encodeURIComponent(key)}/payment/cod-advance`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
                body: JSON.stringify(clearing ? { clear: true } : { advance: advanceNum, utr }),
            });
            if (!r.ok) {
                const j = await r.json().catch(() => ({}));
                throw new Error(j.error || `HTTP ${r.status}`);
            }

            const updates = {};
            const what = !clearing
                ? `advance ${rs(advanceNum)} RECEIVED manually · UTR ${utr} · courier collects ${rs(collects)}`
                : `advance cleared · full COD ${rs(total)}`;
            updates[`orders/${key}/audit`] = [
                ...(order.audit || []),
                { action: 'Partial COD updated', oldValue: existing > 0 ? `advance ${rs(existing)}` : 'full COD', newValue: what, timestamp: now, user: who },
            ];

            await update(ref(db), updates);
            // The advance is in: an open advance link must not take it a second time.
            if (!clearing && (order?.payment?.paymentLink?.purpose === 'cod_advance')) {
                const r = await closePaymentLink(order, `Advance recorded manually · UTR ${utr}`);
                if (r?.paidThroughLink) alert(r.double
                    ? 'The customer had ALSO paid the advance through the link. The order is flagged "Double payment" — refund one of them.'
                    : 'The customer had already paid the advance through the link — that payment has been recorded.');
            }
            if (onUpdate) onUpdate();
            onClose();
        } catch (error) {
            console.error('Failed to update partial COD:', error);
            alert('Failed to update: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9998] backdrop-blur-sm p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[92vh] flex flex-col overflow-hidden">
                <div className="flex justify-between items-center p-4 border-b border-slate-100">
                    <h3 className="font-bold text-slate-800 flex items-center gap-2"><Wallet size={17} className="text-indigo-600" /> Partial COD · advance paid manually</h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
                </div>

                <div className="p-6 space-y-4 overflow-y-auto">
                    <div className="rounded-lg border border-indigo-100 bg-indigo-50/60 px-3 py-2 text-[11px] text-indigo-800 leading-snug">
                        For an advance the customer paid <b>directly</b> to our bank / UPI. To collect it online instead,
                        use <b>Payment link…</b> — it records Cashfree's UTR by itself.
                    </div>

                    <div className="flex items-center justify-between">
                        <div>
                            <div className="text-[11px] uppercase font-bold text-slate-400">Order</div>
                            <div className="text-slate-900 font-mono font-bold">{order.id}</div>
                        </div>
                        <div className="text-right">
                            <div className="text-[11px] uppercase font-bold text-slate-400">Order total</div>
                            <div className="text-slate-900 font-bold">{rs(total)}</div>
                        </div>
                    </div>

                    {/* Money mentioned in a remark but never recorded here. */}
                    {remarkFlag.suspect && (
                        <div className="rounded-lg border-2 border-orange-400 bg-orange-50 px-3 py-2">
                            <div className="flex items-center gap-2 text-orange-900 text-sm font-bold">
                                <AlertCircle size={16} /> An advance may already have been collected
                            </div>
                            <div className="text-[11px] text-orange-800 mt-1 leading-snug">{remarkFlag.message}</div>
                            <div className="text-[11px] text-orange-700/80 mt-1 italic">Remark: “{remarkFlag.text}”</div>
                        </div>
                    )}

                    {/* Who put this advance on the order. */}
                    {existing > 0 && order?.payment?.codAdvanceSetBy && (
                        <div className="text-[11px] text-slate-500">
                            Advance of {rs(existing)} set by <b className="text-slate-700">{order.payment.codAdvanceSetBy}</b>
                            {order?.payment?.codAdvanceSetAt ? ` on ${new Date(order.payment.codAdvanceSetAt).toLocaleString('en-IN')}` : ''}
                        </div>
                    )}

                    {blocked ? (
                        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 flex gap-2">
                            <AlertCircle size={16} className="shrink-0 mt-0.5" /> <span>{blocked}</span>
                        </div>
                    ) : alreadyPaid ? (
                        <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-emerald-800 text-sm font-semibold">
                            <CheckCircle2 size={16} /> {rs(existing)} advance already received{order?.payment?.codAdvanceReference ? ` · UTR ${order.payment.codAdvanceReference}` : ''}. The courier collects {rs(codCollectable(order))}.
                        </div>
                    ) : (
                        <>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Advance received (₹)</label>
                                <input type="number" min="0" value={advance} onChange={(e) => setAdvance(e.target.value)}
                                    placeholder="0 = full COD"
                                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 outline-none" />
                                <div className="text-[11px] text-slate-400 mt-0.5">Set 0 to remove an unpaid advance and make this a normal full-COD order.</div>
                            </div>

                            {tooBig && (
                                <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800 font-semibold flex gap-2">
                                    <AlertCircle size={16} className="shrink-0 mt-0.5" />
                                    <span>The advance cannot be the whole order ({rs(total)}). For a fully prepaid order, change the payment mode instead.</span>
                                </div>
                            )}

                            {!clearing && !tooBig && (
                                <>
                                    <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 space-y-1 text-sm">
                                        <div className="flex justify-between"><span className="text-indigo-700">Received up front</span><span className="font-bold text-indigo-900">{rs(advanceNum)}</span></div>
                                        <div className="flex justify-between pt-1 border-t border-indigo-200"><span className="text-indigo-700 font-semibold">Courier collects on delivery</span><span className="font-bold text-indigo-900">{rs(collects)}</span></div>
                                    </div>
                                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2">
                                        <label className="block text-sm font-semibold text-slate-700">UTR / UPI reference of the {rs(advanceNum)} advance <span className="text-rose-600">*</span></label>
                                        <input value={reference} onChange={(e) => setReference(e.target.value.replace(/\s/g, ''))}
                                            placeholder="e.g. 629442423301"
                                            className={`w-full px-3 py-2 border rounded-lg text-sm font-mono outline-none focus:border-indigo-500 ${reference && !utrOk ? 'border-rose-400 bg-rose-50' : 'border-slate-300'}`} />
                                        <div className="text-[11px] text-slate-500">
                                            Saving records the advance as RECEIVED and lets the order go to a courier.
                                        </div>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </div>

                <div className="p-4 border-t border-slate-100 flex justify-end gap-3 bg-slate-50">
                    <Button variant="ghost" onClick={onClose} disabled={loading}>{alreadyPaid || blocked ? 'Close' : 'Cancel'}</Button>
                    {!alreadyPaid && !blocked && (
                        <Button icon={Save} onClick={handleSave} loading={loading} disabled={tooBig || (!clearing && !utrOk)}>
                            {clearing ? 'Save (full COD)' : 'Record advance received'}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PartialCodModal;
